// Configuración de horario por usuario. La usan el administrador y los supervisores
// cuando la empresa activó "Calendario diferente para cada usuario". Cada quien
// define aquí: si acepta citas, su horario (días/horas/almuerzo/festivos) y a qué
// servicios se le puede pedir cita. Responsive (móvil y escritorio).
import { useState, useEffect } from 'react';
import { theme } from '../theme';
import { useIsMobile } from '../hooks/useMediaQuery';

const DAYS_ES = [
  { key: 'lunes', label: 'Lunes' },
  { key: 'martes', label: 'Martes' },
  { key: 'miercoles', label: 'Miércoles' },
  { key: 'jueves', label: 'Jueves' },
  { key: 'viernes', label: 'Viernes' },
  { key: 'sabado', label: 'Sábado' },
  { key: 'domingo', label: 'Domingo' },
];

const DEFAULT_SCHEDULE = {
  ...Object.fromEntries(
    DAYS_ES.map(({ key }, i) => [key, i < 5
      ? { enabled: true, start: '08:00', end: '18:00', lunch: null }
      : { enabled: false, start: '08:00', end: '18:00', lunch: null }
    ])
  ),
  includeHolidays: false,
};

const timeInputStyle = {
  padding: '5px 8px',
  backgroundColor: theme.colors.background,
  border: `1px solid ${theme.colors.border}`,
  borderRadius: '6px', color: theme.colors.text,
  fontSize: '0.82rem', outline: 'none', width: '82px',
};

function HorarioConfig({ token }) {
  const isMobile = useIsMobile();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(true);
  const [perUserCalendar, setPerUserCalendar] = useState(false);
  const [services, setServices] = useState([]);
  const [citaEnabled, setCitaEnabled] = useState(false);
  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
  const [serviceIds, setServiceIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    fetch('/api/appointments/my-config', { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) {
          setPerUserCalendar(!!d.per_user_calendar);
          setServices(Array.isArray(d.services) ? d.services : []);
          setCitaEnabled(!!d.config?.cita_enabled);
          setSchedule({ ...DEFAULT_SCHEDULE, ...(d.config?.schedule || {}) });
          setServiceIds(Array.isArray(d.config?.service_ids) ? d.config.service_ids.map(String) : []);
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  const updateDay = (key, field, value) =>
    setSchedule(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));

  const toggleLunch = (key, enable) =>
    setSchedule(prev => ({ ...prev, [key]: { ...prev[key], lunch: enable ? { start: '12:00', end: '13:00' } : null } }));

  const updateLunch = (key, field, value) =>
    setSchedule(prev => ({ ...prev, [key]: { ...prev[key], lunch: { ...prev[key].lunch, [field]: value } } }));

  const toggleService = (id) => {
    const sid = String(id);
    setServiceIds(prev => prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]);
  };

  const save = async () => {
    setSaving(true);
    setMsg('');
    const res = await fetch('/api/appointments/my-config', {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ cita_enabled: citaEnabled, schedule, service_ids: serviceIds }),
    });
    setMsg(res.ok ? '✓ Configuración guardada' : 'Error al guardar');
    setSaving(false);
  };

  const cardStyle = {
    backgroundColor: theme.colors.surface,
    borderRadius: '14px',
    border: `1px solid ${theme.colors.border}`,
    padding: isMobile ? '1.25rem' : '2rem',
    boxShadow: `0 4px 24px ${theme.colors.glowSubtle}`,
  };

  if (loading) {
    return <main style={{ padding: '2rem', color: theme.colors.textMuted }}>Cargando…</main>;
  }

  return (
    <main style={{ padding: isMobile ? '1.25rem 1rem' : '2rem 1.5rem', maxWidth: '900px', margin: '0 auto' }}>
      <div style={cardStyle}>
        <h1 style={{ color: theme.colors.primary, fontSize: '1.5rem', marginBottom: '0.3rem' }}>Configuración horario</h1>
        <p style={{ color: theme.colors.textMuted, fontSize: '0.9rem', marginBottom: '1.5rem' }}>
          Define tu propia disponibilidad para las citas. Los clientes podrán agendar contigo según lo que configures aquí.
        </p>

        {!perUserCalendar && (
          <div style={{ backgroundColor: `${theme.colors.primary}11`, border: `1px solid ${theme.colors.primary}`, borderRadius: '10px', padding: '1rem', color: theme.colors.text, fontSize: '0.9rem' }}>
            Tu empresa no tiene activada la función de <strong>calendario por usuario</strong>. Mientras esté desactivada, las citas usan el calendario global de la empresa y esta configuración no aplica.
          </div>
        )}

        {perUserCalendar && (
          <>
            {/* Habilitar citas */}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', marginBottom: '1.5rem' }}>
              <input
                type="checkbox"
                checked={citaEnabled}
                onChange={e => setCitaEnabled(e.target.checked)}
                style={{ accentColor: theme.colors.primary, marginTop: '3px' }}
              />
              <span style={{ color: theme.colors.text, fontSize: '0.95rem' }}>
                Habilitar pedir citas
                <span style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.8rem', marginTop: '2px' }}>
                  Si está activo, apareces en el listado “Agendar cita con…” del link público (solo para los servicios que marques abajo).
                </span>
              </span>
            </label>

            {/* Horario */}
            <div style={{ backgroundColor: theme.colors.background, borderRadius: '10px', border: `1px solid ${theme.colors.border}`, padding: '1.2rem', marginBottom: '1.5rem' }}>
              <h2 style={{ color: theme.colors.primary, fontSize: '1rem', marginBottom: '1rem' }}>Mi horario</h2>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '1rem' }}>
                <input
                  type="checkbox"
                  checked={schedule.includeHolidays === true}
                  onChange={e => setSchedule(prev => ({ ...prev, includeHolidays: e.target.checked }))}
                  style={{ accentColor: theme.colors.primary }}
                />
                <span style={{ color: theme.colors.text, fontSize: '0.85rem' }}>Atender en días festivos</span>
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {DAYS_ES.map(({ key, label }) => {
                  const day = schedule[key] || { enabled: false, start: '08:00', end: '18:00', lunch: null };
                  return (
                    <div key={key} style={{ borderBottom: `1px solid ${theme.colors.border}22`, paddingBottom: '0.6rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', minWidth: '90px' }}>
                          <input
                            type="checkbox"
                            checked={day.enabled}
                            onChange={e => updateDay(key, 'enabled', e.target.checked)}
                            style={{ accentColor: theme.colors.primary }}
                          />
                          <span style={{ color: day.enabled ? theme.colors.text : theme.colors.textMuted, fontSize: '0.88rem', fontWeight: day.enabled ? 600 : 400 }}>{label}</span>
                        </label>
                        {day.enabled && (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <span style={{ color: theme.colors.textMuted, fontSize: '0.78rem' }}>Inicio</span>
                              <input type="time" value={day.start} onChange={e => updateDay(key, 'start', e.target.value)} style={timeInputStyle} />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <span style={{ color: theme.colors.textMuted, fontSize: '0.78rem' }}>Fin</span>
                              <input type="time" value={day.end} onChange={e => updateDay(key, 'end', e.target.value)} style={timeInputStyle} />
                            </div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={!!day.lunch}
                                onChange={e => toggleLunch(key, e.target.checked)}
                                style={{ accentColor: theme.colors.primary }}
                              />
                              <span style={{ color: theme.colors.textMuted, fontSize: '0.78rem' }}>Almuerzo</span>
                            </label>
                            {day.lunch && (
                              <>
                                <input type="time" value={day.lunch.start} onChange={e => updateLunch(key, 'start', e.target.value)} style={timeInputStyle} />
                                <span style={{ color: theme.colors.textMuted, fontSize: '0.78rem' }}>–</span>
                                <input type="time" value={day.lunch.end} onChange={e => updateLunch(key, 'end', e.target.value)} style={timeInputStyle} />
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Servicios */}
            <div style={{ backgroundColor: theme.colors.background, borderRadius: '10px', border: `1px solid ${theme.colors.border}`, padding: '1.2rem', marginBottom: '1.5rem' }}>
              <h2 style={{ color: theme.colors.primary, fontSize: '1rem', marginBottom: '0.3rem' }}>Servicios que atiendo</h2>
              <p style={{ color: theme.colors.textMuted, fontSize: '0.8rem', margin: '0 0 1rem' }}>
                Marca a cuáles servicios te pueden pedir cita. Solo apareces en el link público para los servicios marcados.
              </p>
              {services.length === 0 && (
                <p style={{ color: theme.colors.textMuted, fontSize: '0.85rem' }}>No hay servicios creados en la empresa.</p>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {services.map(s => (
                  <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={serviceIds.includes(String(s.id))}
                      onChange={() => toggleService(s.id)}
                      style={{ accentColor: theme.colors.primary }}
                    />
                    <span style={{ color: theme.colors.text, fontSize: '0.9rem' }}>{s.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <button
              onClick={save}
              disabled={saving}
              style={{
                padding: '10px 24px',
                backgroundColor: saving ? theme.colors.border : theme.colors.primary,
                color: theme.colors.background,
                border: 'none', borderRadius: '7px',
                fontSize: '0.92rem', fontWeight: 700,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Guardando…' : 'Guardar configuración'}
            </button>
            {msg && <p style={{ color: msg.startsWith('✓') ? theme.colors.success : theme.colors.error, fontSize: '0.85rem', marginTop: '0.6rem' }}>{msg}</p>}
          </>
        )}
      </div>
    </main>
  );
}

export default HorarioConfig;
